import React from 'react';
import Icon from './icons';
import MasterPanel from './MasterPanel';
import BankGuideSteps from './BankGuideSteps';
import GenDocRow from './GenDocRow';
import { MasterCollapse, MasterChip, getUp, getUpKey, OkFile, UpBtn, RefFile, Wait, S3Row, BulkSaveBtn, s3UploadHandler, dispName } from './stepcommon';
import { collectGenFiles, openS3, downloadUploadFile, fetchCollectBlob } from './docgen';
import type { Project } from './types';

interface Props {
  p: Project;
  onPatch: (patch: Partial<Project>) => void;
  toast: (m: string) => void;
  masterOpen: boolean;
  onToggleMaster: () => void;
  onPv: (k: string, idx?: number) => void;
  onVer: (k: string) => void;
  onFilePv: (name: string, title?: string) => void;
}

export default function Step5({ p, onPatch, toast, masterOpen, onToggleMaster, onPv, onVer }: Props) {   // onFilePv: 승인공문이 S3 열기로 바뀌어 미사용 (2026-09-24)
  const up = (k: string, title: string, label: string) => {
    const cur = getUp(p, 's5', k);
    const key = getUpKey(p, 's5', k);
    const disp = cur ? dispName(cur, title, p.org || '') : cur;
    const save = s3UploadHandler(p, 's5', k, onPatch, toast, title);
    return cur
      ? <OkFile name={disp} onPv={key ? () => openS3(key, disp, true).catch((e) => toast((e as Error).message)) : undefined} onDl={() => downloadUploadFile(p, key || '', disp, toast)} onRe={save} />
      : <UpBtn label={label} onUp={save} />;
  };
  const gd = (k: string, seal: boolean) => <GenDocRow p={p} k={k} seal={seal} onPatch={onPatch} onVer={onVer} onPv={onPv} toast={toast} />;
  const approvalFn = getUp(p, 's4', 'approval');
  // 화면 표시 행 중 누락분 보완 (업로드본/앞단계 연동본)
  const upBulk = (store: 's4' | 's5', k: string, title: string) => {
    const fn = getUp(p, store, k);
    if (!fn) return [];
    const nm = dispName(fn, title, p.org || '');
    const key = getUpKey(p, store, k);
    return key ? [{ key, name: nm }] : [{ key: '', name: nm, getBlob: () => fetchCollectBlob(p, fn) }];
  };
  // 은행 구비서류 안내장 — 자동 생성(2026-10-31) · 예전에 직접 올린 것은 아래에 참고로만
  const bankUp = getUp(p, 's5', 'bankguide');
  const bulkFiles = [
    ...collectGenFiles(p, ['inkam', 'roster', 'rule', 'bankguide']),
    ...upBulk('s5', 'unique', '고유번호증'),
    ...upBulk('s4', 'approval', '개인투자조합 결성계획 승인 안내'),
  ];

  return (
    <div className="sact">
      <div className="sact-h" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span>🏦 조합통장 개설서류 발송</span>
        <BulkSaveBtn files={bulkFiles} toast={toast} pid={p.id} />
      </div>
      <MasterCollapse open={masterOpen} onToggle={onToggleMaster} chip={<MasterChip m={p.master} />} title="📋 공통정보 (마스터)" sub="열기를 누르면 펼쳐집니다 · 3단계 입력 그대로 연동">
        <MasterPanel p={p} onPatch={onPatch} toast={toast} withAnswers={false} regenKeys={['inkam', 'bankguide']} regenLabel="마스터값으로 5단계 문서 재생성" regenToast="마스터값으로 5단계 문서 재생성됨" />
      </MasterCollapse>
      <div className="lbl" style={{ marginTop: 14 }}>발송 서류 (6) · 앞단계 자동 연동 + 자동 생성</div>
      <S3Row no={1} title="고유번호증" note="세무서 발급 · 직접 업로드">{up('unique', '고유번호증', '고유번호증 업로드')}</S3Row>
      <S3Row no={2} title="개인투자조합 결성계획 승인 안내" note="4단계(고유번호증 발급)에서 업로드한 승인 공문 자동 연동">
        {approvalFn
          ? <RefFile name={approvalFn} tag="4단계 자동 연동"
              onPv={() => openS3(getUpKey(p, 's4', 'approval'), approvalFn, true).catch((e) => toast((e as Error).message))}
              onDl={() => downloadUploadFile(p, getUpKey(p, 's4', 'approval'), approvalFn, toast)} />
          : <Wait msg="4단계에서 승인 공문을 업로드하면 자동 연동됩니다" />}
      </S3Row>
      <S3Row no={3} title="조합명의 계좌 개설 은행 구비서류 안내장" note="마스터 · 등록정보로 자동 생성(대표님 · 조합명 · 출자금 · 법인 이체액) · 바뀌면 재생성">
        <BankGuideSteps p={p} onPatch={onPatch} toast={toast} />
        {gd('bankguide', false)}
        {bankUp && <div style={{ marginTop: 6 }}><RefFile name={dispName(bankUp, '조합명의 계좌 개설 은행 구비서류 안내장', p.org || '')} tag="예전에 직접 올린 것"
          onPv={() => { const bk = getUpKey(p, 's5', 'bankguide'); if (bk) openS3(bk, bankUp, true).catch((e) => toast((e as Error).message)); else fetchCollectBlob(p, bankUp).then((b) => window.open(URL.createObjectURL(b), '_blank', 'noopener')).catch((e) => toast((e as Error).message)); }}
          onDl={() => downloadUploadFile(p, getUpKey(p, 's5', 'bankguide'), bankUp, toast)} /></div>}
      </S3Row>
      <S3Row no={4} title="조합원 명부" note="4단계 명부 자동 연동 · 여기서 수정하면 4단계도 반영">{gd('roster', false)}</S3Row>
      <S3Row no={5} title="조합인감등록부" link={{ label: '모두싸인 바로가기', href: 'https://modusign.co.kr/' }} note="마스터로 자동 생성(Word·PDF)">
        <GenDocRow p={p} k="inkam" seal={false} onPatch={onPatch} onVer={onVer} onPv={onPv} toast={toast} />
      </S3Row>
      <S3Row no={6} title="개인투자조합 규약" note="3단계 규약 자동 연동 · 여기서 수정하면 앞단계도 반영">{gd('rule', false)}</S3Row>
      <button className="btn pri sm" style={{ width: '100%', justifyContent: 'center', marginTop: 6 }} onClick={() => {
        // 예전엔 아무 것도 안 하고 안내만 띄우던 버튼 — 은행 제출 6종 중 빠진 것을 세어 알려준다
        const miss: string[] = [];
        if (!getUp(p, 's5', 'unique')) miss.push('고유번호증');
        if (!approvalFn) miss.push('승인 공문(4단계)');
        if (!(p.master && p.master.gp && p.master.gp.name)) miss.push('은행 구비서류 안내장(3단계 마스터 GP 이름)');
        toast(miss.length ? '아직 빠진 서류: ' + miss.join(', ') : '은행 제출 서류 6종이 모두 준비됐습니다 · 상단 「전체 다운로드」로 받아 은행에 제출하세요');
      }}>
        <Icon n="send" style={{ width: 13, height: 13 }} /> 통장 개설서류 점검
      </button>
    </div>
  );
}
